import camelCaseKeysRecursive from 'camelcase-keys-recursive';

import { apiRequest } from 'platform/utilities/api';
import localStorage from 'platform/utilities/storage/localStorage';
import { ssoKeepAliveSession } from 'platform/utilities/sso';
import { removeInfoToken } from 'platform/utilities/oauth/utilities';

const commonServices = {
  MVI: 'MVI',
  VA_PROFILE: 'VAProfile',
};

function getErrorStatusDesc(code) {
  if (code === 404) {
    return 'NOT_FOUND';
  }

  if (code === 401) {
    return 'NOT_AUTHORIZED';
  }

  return 'SERVER_ERROR';
}

export function mapRawUserDataToState(json) {
  const {
    data: {
      attributes: {
        userAccount: { id: accountUuid } = {},
        inProgressForms: savedForms,
        prefillsAvailable,
        profile: {
          initialSignIn,
          signIn,
          birthDate: dob,
          email,
          firstName: first,
          gender,
          icn,
          lastName: last,
          loa,
          middleName: middle,
          multifactor,
          verified,
          claims,
          edipi,
          preferredName,
          onboarding: profileOnboarding,
        },
        onboarding,
        services,
        vaProfile,
        vet360ContactInformation,
        veteranStatus,
        session,
      },
    },
    meta,
  } = camelCaseKeysRecursive(json);

  const userState = {
    accountType: loa.current,
    accountUuid,
    initialSignIn,
    dob,
    email,
    gender,
    icn,
    isCernerPatient: vaProfile?.isCernerPatient,
    loa,
    multifactor,
    prefillsAvailable,
    savedForms,
    services,
    signIn,
    userFullName: {
      first,
      middle,
      last,
    },
    preferredName,
    onboarding: onboarding ?? profileOnboarding ?? { show: false },
    verified,
    claims,
    edipi,
    vapContactInfo: vet360ContactInformation,
    session,
    veteranStatus: {},
    userAtPretransitionedOhFacility:
      vaProfile?.ohMigrationInfo?.userAtPretransitionedOhFacility,
    userFacilityReadyForInfoAlert:
      vaProfile?.ohMigrationInfo?.userFacilityReadyForInfoAlert,
    userFacilityMigratingToOh:
      vaProfile?.ohMigrationInfo?.userFacilityMigratingToOh,
    migrationSchedules: vaProfile?.ohMigrationInfo?.migrationSchedules,
    emergencyCutoverLock:
      vaProfile?.ohMigrationInfo?.emergencyCutoverLock || [],
  };

  if (meta && veteranStatus === null) {
    const errorStatus = meta.errors.find(
      error => error.externalService === commonServices.VA_PROFILE,
    ).status;
    userState.veteranStatus.status = getErrorStatusDesc(errorStatus);
  } else {
    userState.veteranStatus = { ...veteranStatus };
  }

  if (meta && vaProfile === null) {
    const errorStatus = meta.errors.find(
      error => error.externalService === commonServices.MVI,
    ).status;
    userState.status = getErrorStatusDesc(errorStatus);
  } else {
    userState.status = vaProfile.status;
    if (vaProfile.facilities) {
      userState.facilities = vaProfile.facilities;
    }
    userState.vaPatient = vaProfile.vaPatient;
    userState.mhvAccountState = vaProfile.mhvAccountState;
    userState.schedulingPreferencesPilotEligible =
      vaProfile.schedulingPreferencesPilotEligible;
  }

  // This one is checking userState because there's no extra mapping and it's
  // easier to leave the mocking code the way it is
  if (meta && userState.vapContactInfo === null) {
    const errorStatus = meta.errors.find(
      error => error.externalService === commonServices.VA_PROFILE,
    ).status;
    userState.vapContactInfo = { status: getErrorStatusDesc(errorStatus) };
  }

  return userState;
}

// Flag to indicate an active session for initial page loads.
// It's distinct from the currentlyLoggedIn state, which
// serves as confirmation that the user is logged in and
// as a trigger to properly update any components that subscribe to it.
export const hasSession = () => localStorage.getItem('hasSession');

// hasSessionSSO will only ever be true or false.
// Wrapping in JSON.parse enables making boolean checks with this function call.
export const hasSessionSSO = () =>
  JSON.parse(localStorage.getItem('hasSessionSSO'));

/**
 * The header avatar's initials: first and last initial, or nothing when
 * either name is missing or blank.
 */
export const getUserInitials = (firstName, lastName) => {
  const first = firstName?.trim();
  const last = lastName?.trim();
  return first && last ? `${first[0]}${last[0]}`.toUpperCase() : undefined;
};

export function setupProfileSession(userProfile) {
  const { firstName, signIn, preferredName } = userProfile;
  localStorage.setItem('hasSession', true);
  if (signIn?.ssoe) {
    ssoKeepAliveSession();
  }

  // Since localStorage coerces everything into String,
  // this avoids setting the first name to the string 'null'.
  if (firstName) localStorage.setItem('userFirstName', firstName);
  if (preferredName) localStorage.setItem('preferredName', preferredName);
}

export function teardownProfileSession() {
  [
    'hasSession',
    'userFirstName',
    'userInitials',
    'preferredName',
    'sessionExpiration',
    'hasSessionSSO',
    'sessionExpirationSSO',
    'atExpires',
  ].forEach(key => localStorage.removeItem(key));
  removeInfoToken();
  sessionStorage.clear();
}

const SERVER_ERROR_REGEX = /^5\d{2}$/;
const CLIENT_ERROR_REGEX = /^4\d{2}$/;

export async function getData(apiRoute, options) {
  try {
    const response = await apiRequest(apiRoute, options);
    return response.data.attributes;
  } catch (error) {
    return error;
  }
}

export const isServerError = errCode => SERVER_ERROR_REGEX.test(errCode);

export const isClientError = errCode => CLIENT_ERROR_REGEX.test(errCode);
